import { call, put, takeEvery, select, delay } from "redux-saga/effects";
import * as selectors from "./selectors";
import {
  REMOVE_FROM_LIST,
  ADD_TO_LIST,
  CLICK_CREATE,
  CLOSE_BOXES,
  CLOSE_OTHER_BOXES,
  CREATE_LIST,
  ICON_CLICK,
  INIT,
  SET_USER_ID,
  addList,
  closeBox,
  closeBoxes,
  closeOtherBoxes,
  initArtwork,
  openBox,
  setAddingToList,
  setBoxContent,
  setCreatingList,
  setInputError,
  setInputVisibility,
  setLists,
  setRemovingFromList,
  setUserId
} from "./actions";

/**
 * Forms a POST request for fetch.
 *
 * @name async
 * @function
 * @param   {string} url  Url to call.
 * @param   {object} data Data to POST.
 * @returns {object}      Response object.
 */
const postData = async (url = "", data = {}) => {
  const response = await fetch(url, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-WP-Nonce": gage.nonce
    },
    redirect: "follow",
    referrer: "no-referrer",
    body: JSON.stringify(data)
  });
  return await response.json();
};

/**
 * Initialize the application for the whole site.
 *
 * @name init
 * @function
 */
function* init() {
  const userId = yield select(selectors.userId);
  if (!userId) {
    yield put(setUserId(gage.userId));
    yield put(initArtwork());
  }
}

/**
 * Fetches the current user's favourites lists from the REST API.
 *
 * @name getFavLists
 * @function
 * @param {int} {payload} User id.
 */
function* getLists({ payload }) {
  let response = yield fetch(`/wp-json/wp/v2/suosikit?author=${payload}`);
  let data = yield response.json();
  yield put(setLists(data));
}

/**
 * Handles icon click
 *
 * @name handleIconClick
 * @function
 * @param {int}    art     Id Art ID.
 * @param {string} content Content of action box.
 */
function* handleIconClick({ payload: { artId, content } }) {
  yield put(closeOtherBoxes(artId));
  const boxVisible = yield select(state => selectors.boxVisible(state)(artId));
  const copiedVisible = yield select(state =>
    selectors.copiedVisible(state)(artId)
  );
  const addVisible = yield select(state => selectors.addVisible(state)(artId));
  const removeVisible = yield select(state =>
    selectors.removeVisible(state)(artId)
  );

  if (boxVisible && copiedVisible && content === "copied") {
    return;
  }
  if (boxVisible && addVisible && content === "add") {
    yield put(closeBoxes());
    return;
  }
  if (boxVisible && removeVisible && content === "remove") {
    yield put(closeBoxes());
    return;
  }

  if (
    (copiedVisible && content !== "copied") ||
    (addVisible && content !== "add") ||
    (removeVisible && content !== "remove")
  ) {
    yield put(closeBoxes());
    yield delay(200);
    yield put(setBoxContent({ artId, content }));
    yield put(openBox(artId));
    return;
  }

  if (!boxVisible) {
    yield put(setBoxContent({ artId, content }));
    yield put(openBox(artId));
    if (content === "copied") {
      yield delay(5000);
      yield call(maybeCloseBox, artId);
    }
  }
}

/**
 * Closes box if need be.
 *
 * @name maybeCloseBox
 * @function
 * @param {int} artId Art ID.
 */
function* maybeCloseBox(artId) {
  const boxVisible = yield select(state => selectors.boxVisible(state)(artId));
  const copiedVisible = yield select(state =>
    selectors.copiedVisible(state)(artId)
  );
  if (boxVisible && copiedVisible) {
    yield put(closeBox(artId));
  }
}

/**
 * Handles creating lists.
 *
 * @name handleCreateList
 * @function
 * @param {string} {payload} List title.
 */
function* handleCreateList({ payload }) {
  const creatingList = yield select(selectors.creatingList);
  if (creatingList) return;
  yield put(setCreatingList(true));
  try {
    const data = yield postData("/wp-json/wp/v2/suosikit", {
      title: payload,
      status: "publish"
    });
    yield put(addList(data));
    yield put(setInputVisibility(false));
    yield put(setInputError(false));
  } catch (error) {
    console.error(error);
    yield put(setInputError(true));
  } finally {
    yield put(setCreatingList(false));
  }
}

/**
 * Handles create new list clicks.
 *
 * @name handleClickCreate
 * @function
 */
function* handleClickCreate() {
  const inputVisible = yield select(selectors.inputVisible);
  if (!inputVisible) {
    yield put(setInputVisibility(true));
  }
}

/**
 * Hides the input field.
 *
 * @name hideInput
 * @function
 */
function* hideInput() {
  yield put(setInputVisibility(false));
}

/**
 * Handles adding artwork to lists
 *
 * @name handleAddToList
 * @function
 * @param {int} artId Art ID.
 * @param {int} list List ID.
 */
function* handleAddToList({ payload: { artId, listId } }) {
  const addingToList = yield select(state =>
    selectors.addingToList(state)(listId)
  );
  if (addingToList) return;
  yield put(setAddingToList({ listId, adding: true }));
  try {
    const data = yield postData("/wp-json/taiderakentamisessa/v1/fav-lists", {
      method: "add",
      listId,
      artId
    });
    if (data) {
      const userId = yield select(selectors.userId);
      yield call(getLists, { payload: userId });
    }
    yield put(setInputError(false));
  } catch (error) {
    console.error(error);
    yield put(setInputError(true));
  } finally {
    yield put(setAddingToList({ listId, adding: false }));
  }
}

/**
 * Reload page if in favourites list
 *
 * @name maybeDeleteItem
 * @function
 */
const maybeReloadPage = () => {
  if (document.querySelector(".favourites-list")) {
    window.location.reload(true);
  }
};

/**
 * Handles removing artwork from lists.
 *
 * @name handleAddToList
 * @function
 * @param {int} artId Art ID.
 * @param {int} list List ID.
 */
function* handleRemoveFromList({ payload: { artId, listId } }) {
  const removingFromList = yield select(state =>
    selectors.removingFromList(state)(listId)
  );
  if (removingFromList) return;
  yield put(setRemovingFromList({ listId, removing: true }));
  try {
    const data = yield postData("/wp-json/taiderakentamisessa/v1/fav-lists", {
      method: "remove",
      listId,
      artId
    });
    if (data) {
      const userId = yield select(selectors.userId);
      yield call(getLists, { payload: userId });
      maybeReloadPage();
    }
    yield put(setInputError(false));
  } catch (error) {
    console.error(error);
    yield put(setInputError(true));
  } finally {
    yield put(setRemovingFromList({ listId, removing: false }));
  }
}

export default function* saga() {
  yield takeEvery(INIT, init);
  yield takeEvery(SET_USER_ID, getLists);
  yield takeEvery(ICON_CLICK, handleIconClick);
  yield takeEvery(CLICK_CREATE, handleClickCreate);
  yield takeEvery(CLOSE_BOXES, hideInput);
  yield takeEvery(CLOSE_OTHER_BOXES, hideInput);
  yield takeEvery(CREATE_LIST, handleCreateList);
  yield takeEvery(ADD_TO_LIST, handleAddToList);
  yield takeEvery(REMOVE_FROM_LIST, handleRemoveFromList);
}
